import {Box, CircularProgress, Drawer, Theme} from '@mui/material';
import {Profile} from '../../types/api';
import {useTranslation} from 'react-i18next';
import React, {Suspense} from 'react';
import {coverWidth} from '../../lib/style';
import {lazyWithRetry} from '../../lib/lazyWithRetry';
import {ErrorBoundary} from '@sentry/react';
import ProfileViewContent from './ProfileViewContent.tsx';
import {PreviewProfileProps} from './types.ts';

const ExchangeForm = lazyWithRetry(
    'ExchangeForm',
    () => import('./Exchange/ExchangeForm.tsx'),
);

type Props = {
    profile: Profile;
    safeKey: string;
    showExchange: VoidFunction;
    closeExchange: VoidFunction;
    displayExchange?: boolean;
} & PreviewProfileProps;

export default function ProfileView({
    profile,
    safeKey,
    isPreview = false,
    userLocales,
    showExchange,
    closeExchange,
    displayExchange,
}: Props) {
    const {t} = useTranslation('app');

    return (
        <>
            <Box
                sx={{
                    backgroundColor: 'background.default',
                }}
            >
                <ProfileViewContent
                    profile={profile}
                    isPreview={isPreview}
                    userLocales={userLocales}
                    showExchangeModal={showExchange}
                />
                {profile.options?.exchanges && (
                    <Drawer
                        sx={theme => ({
                            zIndex: theme.zIndex.drawer + 2,
                        })}
                        anchor="bottom"
                        keepMounted={true}
                        open={displayExchange}
                        onClose={closeExchange}
                        slotProps={{
                            paper: {
                                sx: theme => ({
                                    ...exchangeDrawerPaperSx(theme),
                                    maxWidth: coverWidth,
                                }),
                            },
                        }}
                    >
                        <ErrorBoundary
                            fallback={
                                <>
                                    {t(
                                        'app:common.failed_chunk',
                                        'Failed to load, please reload the page!',
                                    )}
                                </>
                            }
                        >
                            <Suspense
                                fallback={
                                    <div
                                        style={{
                                            textAlign: 'center',
                                        }}
                                    >
                                        <CircularProgress />
                                    </div>
                                }
                            >
                                <ExchangeForm
                                    profile={profile}
                                    onClose={closeExchange}
                                    profileSafeKey={safeKey!}
                                    hasPaperScan={
                                        profile.options?.paperScan ?? false
                                    }
                                />
                            </Suspense>
                        </ErrorBoundary>
                    </Drawer>
                )}
            </Box>
        </>
    );
}

export function exchangeDrawerPaperSx(theme: Theme) {
    return {
        py: {
            xs: 2,
            sm: 4,
        },
        px: {
            xs: 2,
            sm: 4,
        },
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
        margin: 'auto',
        width: '100%',
    };
}
